/* eslint-disable import/prefer-default-export */
import { createAsyncThunk } from '@reduxjs/toolkit';

import { createProductDraftFromMaster } from './createProductDraftFromMaster';
import { createVariantDraftFromMaster } from './createVariantDraftFromMaster';
import { getDraftSyncGroups } from './helpers/listingSyncHelpers';
import {
  splitDraftToastChannelIds,
  showSplitDraftToasts,
} from './helpers/showDraftForChannelToast';

/**
 * Orchestration-only thunk: no own reducer, SDK calls, or Toast.
 * Routes draft sync through createProductDraftFromMaster and createVariantDraftFromMaster
 * based on getDraftSyncGroups.
 */
export const syncProductToDraft = createAsyncThunk(
  'products/syncProductToDraft',
  async ({
    productId,
    channelIds,
    draftMedia = {},
    overrideExisting = false,
  }, { dispatch, getState, extra: { Toast } }) => {
    const productsState = getState().products;
    const product = productsState.items.find(({ _id }) => _id === productId);
    const { createdChannelIds, updatedChannelIds } = splitDraftToastChannelIds({
      product,
      productsState,
      channelIds,
    });
    const groups = getDraftSyncGroups({ product, productsState, channelIds });

    if (groups.channelsWithoutListing.length) {
      await dispatch(createProductDraftFromMaster({
        productId,
        channelIds: groups.channelsWithoutListing,
        draftMedia,
        overrideExisting,
        suppressToast: true,
      })).unwrap();
    }

    const syncExistingListingChannel = async ({ channelId, missingVariantIds }) => {
      if (missingVariantIds.length) {
        await dispatch(createVariantDraftFromMaster({
          productId,
          channelIds: [channelId],
          masterVariantIds: missingVariantIds,
          draftMedia,
          overrideExisting,
          suppressToast: true,
        })).unwrap();
      }

      await dispatch(createProductDraftFromMaster({
        productId,
        channelIds: [channelId],
        draftMedia,
        overrideExisting,
        suppressToast: true,
      })).unwrap();
    };

    await Promise.all([
      ...groups.publishedListingChannels.map(syncExistingListingChannel),
      ...groups.draftListingChannels.map(({ channelId, missingVariantIds }) => (
        syncExistingListingChannel({ channelId, missingVariantIds })
      )),
    ]);

    const { user: { team } } = getState();
    showSplitDraftToasts({
      Toast,
      createdChannelIds,
      updatedChannelIds,
      salesChannels: team?.salesChannels || [],
    });

    return { productId, channelIds };
  },
);
